// @flow
import React from 'react';
import injectSheet from 'react-jss';
import _ from 'shared/lib/gettext';
import io from 'socket.io-client';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import Modal from '~/app/components/Modal';
import Button from '~/app/ui/Button';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import StatusBadge from '~/app/ui/StatusBadge';
import EmptyState from '~/app/ui/EmptyState';
import { Section } from '~/app/ui/Page';
import { RowMenu } from '~/app/ui/Menu';
import { Table, Checkbox } from '~/app/ui/Table';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import Scheduler from './Scheduler';
import ProgressTrack from './ProgressTrack';
import {
  getMinimumMinutesBetweenDeployments,
  getMaximumScheduledDeployments,
  getAppURL
} from 'shared/lib/clientSettings';
import { WEBSOCKET_MSG_ID } from 'shared/constants';
import getDisplayPath from 'shared/lib/getDisplayPath';
import { isDroppedConnection } from '~/lib/droppedConnection';

const MINIMUM_MINUTES = getMinimumMinutesBetweenDeployments();
const MAX_DEPLOYMENTS = getMaximumScheduledDeployments();

type Props = {
  classes: Object,
  className?: string,
  scheduledDeployment: Object[],
  pendingDeployment: {
    added: Object[],
    deleted: Object[],
    removed: Object[]
  },
  version: string,
  /** The lists the next version is worked out from are still loading: deploying waits for it. */
  versionLoading?: boolean,
  userId: string,
  deployButtonDisabled: boolean,
  schedule: (versionId: string, deployAt: string, pageVersionIds: string[]) => void,
  cancelPendingDeployment: (pageId: string) => void,
  deploy: (version: string, mode: string, pageVersionIds: string[]) => Promise<Object>,
  /** Refreshes the lists after a deployment started here ends (the socket's end message refreshes them too). */
  onDeployed?: () => void,
  flashMessage: (message: string, messageType?: string) => void
};

type State = {
  selectedPages: string[],
  deployConfirm: boolean,
  // The changes in the deployment started here, while it runs (they can't be changed meanwhile).
  deployingIds: string[],
  deployingVersion: string,
  deployFailure: ?string,
  isScheduling: boolean,
  showScheduler: boolean,
  progress: number,
  allMessages: string[],
  deployError: string,
  cancelTarget: ?Object
}

class PendingDeployment extends React.Component<Props, State> {
  props: Props

  state = {
    selectedPages: [],
    deployConfirm: false,
    deployingIds: [],
    deployingVersion: '',
    deployFailure: null,
    isScheduling: false,
    showScheduler: false,
    progress: 0,
    allMessages: [],
    deployError: '',
    cancelTarget: null
  };

  socket: Object

  handlePageSelectChange = (pageId) => {
    if (this.isLocked(pageId)) return;
    this.setState(({ selectedPages }) => ({
      selectedPages: selectedPages.includes(pageId)
        ? selectedPages.filter(id => id !== pageId)
        : [...selectedPages, pageId]
    }));
  };

  // Every change waiting to deploy, with the id it's selected by (a publish is selected by its version).
  getRows = () => {
    const { pendingDeployment } = this.props;
    if (!pendingDeployment) return [];
    return [
      ...(pendingDeployment.added || []).map(page => ({ page, type: 'publish', id: page.nextVersion ? page.nextVersion._id : null })),
      ...(pendingDeployment.removed || []).map(page => ({ page, type: 'withdraw', id: page._id })),
      ...(pendingDeployment.deleted || []).map(page => ({ page, type: 'delete', id: page._id }))
    ];
  };

  componentDidMount() {
    // Socket used for Scheduled Deployment
    this.socket = io(getAppURL(), {
      reconnectionAttempts: 10
    });
    this.socket.on('disconnect', () => {
      this.setState({ deployError: `${_('RECONNECTING')}` });
    });
    this.socket.on('reconnect_failed', () => {
      this.setState({ deployError: `${_('SERVER_FAILURE')}` });
    });
  }

  componentWillUnmount() {
    this.socket.close();
  }

  // Rows that can't be selected or taken out: the ones in the deployment started here, or every row while
  // a deployment runs that this page didn't start (another tab, a schedule, or after a reload), since it
  // isn't known which changes are in it.
  isLocked = (id) => {
    const { deployingIds } = this.state;
    if (deployingIds.length) return deployingIds.includes(id);
    return Boolean(this.props.deployButtonDisabled);
  }

  getSelectableIds = () => this.getAllPendingIds().filter(id => !this.isLocked(id))

  checkSelectAll = () => {
    const { selectedPages } = this.state;
    const ids = this.getSelectableIds();

    return ids.length > 0 && ids.every(id => selectedPages.includes(id));
  }

  handleSelectAll = () => {
    const { pendingDeployment } = this.props;

    if (isEmpty(pendingDeployment)) {
      return;
    }

    const isAllSelected = this.checkSelectAll();

    let selectedPages = [];

    if (!isAllSelected) {
      selectedPages = this.getSelectableIds();
    }

    this.setState({ selectedPages });
  }

  getSelectedPagePaths = () => {
    const { selectedPages } = this.state;
    const { pendingDeployment } = this.props;
    const selectedPagePaths = [];

    (pendingDeployment.added || [])
      .filter(p => p.nextVersion && selectedPages.includes(p.nextVersion._id))
      .map(p => selectedPagePaths.push(p.path));

    return selectedPagePaths;
  }

  getAllPendingIds = () => {
    return [].concat(
      this.getPendingPublishIds(),
      this.getPendingDeleteIds(),
      this.getPendingWithdrawIds()
    )
  }

  getPendingDeleteIds = () => this.getRows().filter(r => r.type === 'delete').map(r => r.id)

  getPendingPublishIds = () => this.getRows().filter(r => r.type === 'publish' && r.id).map(r => r.id)

  getPendingWithdrawIds = () => this.getRows().filter(r => r.type === 'withdraw').map(r => r.id)

  isUnschedulableTypeSelected = () => {
    const { selectedPages } = this.state;

    const isDeleteSelected = this.getPendingDeleteIds()
      .some(d => selectedPages.includes(d));
    const isWithdrawSelected = this.getPendingWithdrawIds()
      .some(w => selectedPages.includes(w));

    return isDeleteSelected || isWithdrawSelected;
  }

  scheduleShouldBeDisabled = () => {
    const { selectedPages, deployingIds } = this.state;
    const { version, deployButtonDisabled } = this.props;

    return deployingIds.length > 0 || deployButtonDisabled || !version || selectedPages.length === 0 || this.isUnschedulableTypeSelected();
  }

  getCurrentActiveSchedules = () => {
    const { scheduledDeployment } = this.props;
    return filter(scheduledDeployment, schedule => (schedule.status === 'SCHEDULED' || schedule.status === 'NEW' || schedule.status === 'SCHEDULING'));
  }

  handleScheduleClick = () => {
    const currentSchedules = this.getCurrentActiveSchedules();

    if (currentSchedules.length >= MAX_DEPLOYMENTS) {
      this.props.flashMessage(_('ERROR_NO_MORE_THAN_MAX_SCHEDULED').replace('{}', MAX_DEPLOYMENTS), 'ERROR');
      return;
    }
    if (!this.scheduleShouldBeDisabled()) {
      this.setState({ showScheduler: true });
    }
  }

  handleError = (err: string | Error) => {
    console.log(err);
    const errorMessage = err instanceof Error ? err.message : err;

    this.props.flashMessage(errorMessage, 'ERROR');
    this.setState({ deployError: errorMessage });
    this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR);
    this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS);
  }

  handleProgress = ({ progress, msg }) => {
    const { allMessages } = this.state;

    console.log({ progress, msg });
    if (progress >= 1) {
      this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS);
      this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR);
    }

    this.setState({
      progress,
      allMessages: [msg, ...allMessages]
    });
  }


  handleSchedule = (date) => {
    const { schedule, version } = this.props;
    const { selectedPages } = this.state;
    const currentSchedules = this.getCurrentActiveSchedules();

    const schedulesWithConflict = currentSchedules.filter(({ deployAt }) => {
      const targetDate = moment(parseInt(date));
      const startOfRange = moment(parseInt(deployAt)).subtract(MINIMUM_MINUTES, 'minutes');
      const endOfRange = moment(parseInt(deployAt)).add(MINIMUM_MINUTES, 'minutes')

      return targetDate.isBetween(startOfRange, endOfRange);
    });

    // validate if new schedule date is under restriction of minimum time between two deployments
    if (schedulesWithConflict && schedulesWithConflict.length > 0) {
      const message = _('ERROR_NO_SCHEDULE_WITHIN_MIN_MINS')
        .replace('{min}', MINIMUM_MINUTES)
        .replace('{version}', schedulesWithConflict[0].version);

      this.props.flashMessage(message, 'ERROR');
      return;
    }

    this.setState({
      isScheduling: true,
      showScheduler: false
    });
    // All the following channels are used for ONLY private message from the
    // server side. So all clietns listen on these channels but only receive
    // a message when it's relevant to itself.
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleProgress);
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleError);
    schedule(
      version,
      date,
      selectedPages
    )
      .then((result) => {
        const resetState = {
          selectedPages: [],
          deployConfirm: false,
          showScheduler: false,
          progress: 0,
          allMessages: []
        };

        // Failed deployment
        if (!result.data.schedule) {
          this.props.flashMessage('Schedule failed', 'ERROR');
          this.setState({ ...resetState, isScheduling: false });
          this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleProgress);
          this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleError);
        } else {
          this.props.flashMessage(result.data.schedule?.message || 'Deployment scheduled successfully');
          this.setState({
            ...resetState,
            isScheduling: false,
            deployError: ''
          });
          this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleProgress);
          this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleError);
        }
      })
      .catch((e) => {
        this.props.flashMessage(e.message, 'ERROR');
        this.setState({ isScheduling: false });
        this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleProgress);
        this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleError);
        console.error('PendingDeployment.jsx handleSchedule()', e);
      });
  }

  // Deploys the selected changes. No progress window: the status at the top of the page follows the
  // deployment, and the rows in it stay locked until it ends.
  startDeploy = () => {
    const { deploy, version } = this.props;
    const ids = this.state.selectedPages;
    this.setState({
      deployConfirm: false, selectedPages: [], deployingIds: ids, deployingVersion: version, deployFailure: null
    });
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleDeployProgress);
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleDeployError);
    deploy(version, 'SELECTED', ids)
      .then((result) => { if (result && result.data && result.data.deploy) this.finishDeploy(); })
      .catch((e) => {
        // The deployment carries on when only the connection dropped; its end comes over the socket.
        if (isDroppedConnection(e)) return;
        const message = (e && e.message) || '';
        this.failDeploy(message.replace('GraphQL error: ', '') || 'Unknown error.');
      });
  }

  handleDeployProgress = ({ progress }) => {
    if (progress >= 1) this.finishDeploy();
  }

  handleDeployError = (err) => {
    this.failDeploy(err instanceof Error ? err.message : String(err || 'Unknown error.'));
  }

  stopFollowingDeploy = () => {
    this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleDeployProgress);
    this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleDeployError);
  }

  // The end can arrive twice (the socket and the request): only the first one counts.
  finishDeploy = () => {
    const { deployingIds, deployingVersion } = this.state;
    if (!deployingIds.length) return;
    this.stopFollowingDeploy();
    this.setState({ deployingIds: [], deployingVersion: '' });
    if (this.props.onDeployed) this.props.onDeployed();
    this.props.flashMessage(`Deployed v${deployingVersion}.`);
  }

  failDeploy = (message) => {
    if (!this.state.deployingIds.length) return;
    this.stopFollowingDeploy();
    this.setState({ deployingIds: [], deployingVersion: '', deployFailure: message });
    this.props.flashMessage(`The deployment didn’t finish: ${message}`, 'ERROR');
  }

  // Throws on failure, so the confirmation stays open with the reason.
  handleRevertPage = async (pageId, type) => {
    const { cancelPendingDeployment } = this.props;

    await cancelPendingDeployment(pageId);
    this.setState({ selectedPages: [] });
    this.props.flashMessage(type === 'withdraw' ? 'Withdrawal cancelled. The page stays on the site.' : 'Taken out of the next deployment.');
  }

  renderRow = (row) => {
    const { classes: c } = this.props;
    const { selectedPages } = this.state;
    const { page, id, type } = row;
    const kind = KINDS[type];
    const version = type === 'publish' && page.nextVersion ? page.nextVersion.versionId : null;
    const by = type === 'publish' && page.nextVersion ? page.nextVersion.updatedBy : page.updatedBy;
    const note = type === 'publish' && page.nextVersion ? page.nextVersion.notes : null;
    const deploying = this.state.deployingIds.includes(id);
    const locked = this.isLocked(id);
    const checked = deploying || selectedPages.includes(id);
    return (
      <tr key={`${type}-${page._id}`} data-pending={page._id} data-pending-type={type} data-deploying={deploying || undefined} className={checked ? c.selected : undefined}>
        <td className={c.colCheck}>
          <Checkbox
            checked={checked}
            disabled={!id || locked}
            onChange={() => this.handlePageSelectChange(id)}
            label={`Select ${page.title}`}
            title={locked ? 'Can’t change this while a deployment is running' : undefined}
          />
        </td>
        <td className={c.colType}>
          <StatusBadge tone={kind.tone} label={version ? `${kind.label} v${version}` : kind.label} title={kind.hint} />
          {deploying && <div className={c.deploying}>Deploying…</div>}
        </td>
        <td>
          <div className={c.pageTitle}>{page.title}</div>
          <div className={c.path}>{getDisplayPath(page.path)}</div>
        </td>
        <td className={c.colBy}>{by || '—'}</td>
        <td className={c.colNote}>{note ? <span className={c.note} title={note}>{note}</span> : <span className={c.muted}>—</span>}</td>
        <td className={c.colMenu}>
          {type !== 'delete' && (
            <RowMenu
              label={`Actions for ${page.title}`}
              testId={`pending-menu-${page._id}`}
              groups={[[{
                key: 'cancel',
                label: type === 'publish' ? 'Take out of this deployment…' : 'Cancel the withdrawal…',
                icon: 'undo',
                danger: true,
                disabled: locked,
                hint: locked ? 'Not while a deployment is running' : undefined,
                onSelect: () => this.setState({ cancelTarget: row })
              }]]}
            />
          )}
        </td>
      </tr>
    );
  }

  render() {
    const {
      classes: c, version, versionLoading, deployButtonDisabled
    } = this.props;
    const {
      selectedPages, deployConfirm, deployingIds, deployFailure,
      showScheduler, isScheduling,
      progress, allMessages, deployError, cancelTarget
    } = this.state;

    const rows = this.getRows();
    const selectedRows = rows.filter(r => selectedPages.includes(r.id));
    const unschedulable = this.isUnschedulableTypeSelected();
    const deployDisabled = deployButtonDisabled || deployingIds.length > 0 || !version || selectedPages.length === 0;
    // Not ready yet (the next version isn't known). Beside the buttons, a spinner says why they wait (one on Deploy
    // would read as deploying).
    const preparing = !version && Boolean(versionLoading);
    const selectableCount = this.getSelectableIds().length;
    const allChecked = selectableCount > 0 && this.checkSelectAll();

    return (
      <Section
        title={<>Ready to deploy{rows.length > 0 && <span className={c.count}>{rows.length}</span>}</>}
        hint={version || preparing ? (
          // Empty while waiting (the message beside the buttons says why), keeping its height so nothing moves.
          <span className={c.hintLine}>
            {version && <span className={c.hintIn}>The next deployment will be <b className={c.version}>v{version}</b></span>}
          </span>
        ) : null}
        testId="pending-deployment"
        className={c.section}
        actions={(
          <div className={c.deployActions}>
            {preparing && (
              <span className={c.preparing} role="status" data-testid="pending-preparing">
                <span className={c.prepSpinner} aria-hidden />
                Checking scheduled deployments…
              </span>
            )}
            {/* The reason sits on a wrapper: a disabled button gets no hover, so its own title never shows. */}
            <span title={unschedulable ? 'Withdrawals and deletions can’t be scheduled; deploy them now.' : 'Pick a date and time for the selected changes'} data-testid="pending-schedule-hint">
              <Button
                icon="event"
                onClick={this.handleScheduleClick}
                disabled={this.scheduleShouldBeDisabled()}
                data-testid="pending-schedule"
              >
                Schedule…
              </Button>
            </span>
            {/* Say why it can't be clicked: people and AI assistants otherwise wait on a grey button. */}
            <span title={preparing ? 'Available in a moment, once the next version number is worked out'
              : !version ? 'The next version number couldn’t be worked out. Reload the page; if it stays, ask a super admin.'
                : selectedPages.length === 0 ? 'Tick the changes to deploy first' : undefined}>
              <Button
                variant="primary"
                icon="rocket_launch"
                disabled={deployDisabled}
                onClick={() => this.setState({ deployConfirm: true })}
                data-testid="pending-deploy"
              >
                {selectedPages.length > 0 ? `Deploy ${selectedPages.length} now…` : 'Deploy now…'}
              </Button>
            </span>
            {showScheduler &&
              <Scheduler
                onCancel={() => this.setState({ showScheduler: false })}
                pages={this.getSelectedPagePaths()}
                versionNumber={version}
                onConfirm={date => this.handleSchedule(date)}
              />
            }
          </div>
        )}
      >
        {deployFailure && (
          <div className={c.failure} role="alert" data-testid="deploy-failure">
            <i className="material-icons" aria-hidden>error_outline</i>
            <span>The deployment didn’t finish: {deployFailure}. A deployment that stops early isn’t listed in the history below; if this keeps happening, ask whoever looks after Genesis’s hosting before deploying again.</span>
            <button type="button" className={c.failureClose} onClick={() => this.setState({ deployFailure: null })} aria-label="Dismiss">
              <i className="material-icons" aria-hidden>close</i>
            </button>
          </div>
        )}
        <Table label="Changes ready to deploy">
          <thead>
            <tr>
              <th className={c.colCheck}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={selectedPages.length > 0}
                  disabled={selectableCount === 0}
                  onChange={() => this.handleSelectAll()}
                  label="Select all changes"
                />
              </th>
              <th className={c.colType}>Change</th>
              <th>Page</th>
              <th className={c.colBy}>By</th>
              <th className={c.colNote}>Note</th>
              <th className={c.colMenu}><span className={c.hidden}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? rows.map(this.renderRow) : (
              <tr>
                <td colSpan={6}>
                  <EmptyState icon="task_alt" title="Nothing to deploy" compact>
                    Approved publish, withdraw and delete requests wait here until they’re deployed.
                  </EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {deployConfirm &&
          <ConfirmDialog
            title={`Deploy ${selectedRows.length} ${selectedRows.length === 1 ? 'change' : 'changes'} as v${version}?`}
            confirmLabel="Deploy now"
            onConfirm={this.startDeploy}
            onClose={() => this.setState({ deployConfirm: false })}
            testId="deploy-confirm"
          >
            <p>These go to the live site. Progress shows at the top of this page.</p>
            <ul className={c.confirmList}>
              {selectedRows.map(r => (
                <li key={r.id}>
                  <b>{KINDS[r.type].label}</b> {r.page.title} <span className={c.path}>{getDisplayPath(r.page.path)}</span>
                </li>
              ))}
            </ul>
          </ConfirmDialog>
        }
        {cancelTarget &&
          <ConfirmDialog
            danger
            title={cancelTarget.type === 'publish'
              ? `Take “${cancelTarget.page.title}” out of this deployment?`
              : `Cancel withdrawing “${cancelTarget.page.title}”?`}
            confirmLabel={cancelTarget.type === 'publish' ? 'Take it out' : 'Cancel the withdrawal'}
            cancelLabel="Keep it"
            onConfirm={() => this.handleRevertPage(cancelTarget.page._id, cancelTarget.type)}
            onClose={() => this.setState({ cancelTarget: null })}
            testId="pending-cancel-dialog"
          >
            {cancelTarget.type === 'publish'
              ? <p>Its approval is cancelled and the live page stays as it is. To publish these changes later, ask for review again.</p>
              : <p>The page stays on the site.</p>}
          </ConfirmDialog>
        }
        {isScheduling &&
          <Modal
            title="Scheduling"
            onClose={() => this.setState({
              isScheduling: false,
              deployError: ''
            })}
          >
            <div className={c.deployProgressTracker}>
              <ProgressTrack
                progress={progress}
                allMessages={allMessages}
                error={deployError}
              />
            </div>
          </Modal>
        }
      </Section>
    );
  }
}

const KINDS = {
  publish: { label: 'Publish', tone: 'green', hint: 'Goes live with this deployment' },
  withdraw: { label: 'Withdraw', tone: 'amber', hint: 'Comes off the site with this deployment' },
  delete: { label: 'Delete', tone: 'red', hint: 'Deleted in Genesis; comes off the site with this deployment' }
};

const styles = {
  '@keyframes prepSpin': { to: { transform: 'rotate(360deg)' } },
  // Just left of the buttons, out of the row: the header lines its title up with the buttons' text, and this text
  // would move it (the list shifted when the message went).
  preparing: {
    position: 'absolute',
    right: 'calc(100% + 14px)',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: S.muted,
    whiteSpace: 'nowrap',
    '@media (max-width: 900px)': { position: 'static', transform: 'none' }
  },
  // A fixed height for both states, empty or not (the version's monospace font also made the line taller).
  hintLine: { display: 'block', height: 18, lineHeight: '18px', whiteSpace: 'nowrap' },
  '@keyframes hintIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  hintIn: { animation: '$hintIn 200ms ease-out', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } },
  prepSpinner: {
    width: 11,
    height: 11,
    flexShrink: 0,
    borderRadius: '50%',
    border: '2px solid currentColor',
    borderRightColor: 'transparent',
    animation: '$prepSpin 800ms linear infinite'
  },
  section: { marginBottom: 32 },
  count: {
    display: 'inline-block',
    minWidth: 20,
    height: 20,
    marginLeft: 8,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 10,
    background: S.fg,
    color: S.onFg,
    fontSize: 12,
    lineHeight: '20px',
    textAlign: 'center',
    verticalAlign: 2
  },
  version: { fontFamily: S.mono, fontWeight: 500, color: S.fg },
  deployActions: { display: 'flex', alignItems: 'center', gap: 8, position: 'relative', flexWrap: 'wrap' },
  colCheck: { width: 44 },
  colType: { width: 170 },
  colBy: { width: 150, '@media (max-width: 900px)': { display: 'none' } },
  colNote: { width: '28%', '@media (max-width: 1100px)': { display: 'none' } },
  colMenu: { width: 52, textAlign: 'right' },
  hidden: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
  selected: { '& td': { background: `${A.rowSelected} !important` } },
  pageTitle: { fontWeight: 600 },
  path: { fontFamily: S.mono, fontSize: 12, color: S.muted, wordBreak: 'break-all' },
  note: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    fontSize: 13,
    color: '#3A3D44',
    [DARK]: { color: S.fg }
  },
  muted: { color: S.muted },
  deploying: { marginTop: 4, fontSize: 12, fontWeight: 600, color: S.amber },
  failure: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    margin: '0 0 12px',
    padding: '10px 12px',
    borderRadius: A.radius,
    background: '#FBE9E9',
    color: '#8A1C1C',
    fontSize: 14,
    lineHeight: 1.45,
    '& > .material-icons': { fontSize: 18, marginTop: 1 },
    '& > span': { flex: 1 },
    [DARK]: { background: TONES.red.bg, color: TONES.red.fg }
  },
  failureClose: {
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    borderRadius: 4,
    '& .material-icons': { fontSize: 18 },
    '&:focus-visible': A.focus
  },
  confirmList: { margin: '8px 0 0', paddingLeft: 18, maxHeight: 200, overflowY: 'auto', '& li': { margin: '4px 0' } },
  deployProgressTracker: {
    flexGrow: '2'
  }
};

export default injectSheet(styles)(PendingDeployment);
